/**
 * Abstraction over the built-in fetch API. This removes the need for jQuery ajax. See https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API
 * A polyfill for fetch is necessary to support IE11. See https://caniuse.com/#feat=fetch
 *
 * Why not use window.fetch directly? This abstraction has some benefits:
 *  - when we write integration tests, we don't need to setup a fake network, we can just fake this interface
 *  - using this abstraction, we can set default options, such as 'includeCredentials' which allows for authenticated API calls
 */
import { AbortError } from "../../types/AbortError";

export interface IFetcher {
    fetch(request: RequestInfo, options?: RequestInit): Promise<Response>;

    getJSON<TResponseBody>(request: RequestInfo, options?: RequestInit): Promise<TResponseBody>;

    postJSON<TResponseBody>(request: RequestInfo, requestBody?: object | any[], options?: RequestInit): Promise<TResponseBody>;

    get(request: RequestInfo, options?: RequestInit): Promise<boolean>;

    post<TRequestBody>(request: RequestInfo, requestBody?: TRequestBody, options?: RequestInit): Promise<void>;
}

export const defaultHeaders: HeadersInit = {
    // disable caching mechanisms, especially important for IE11 which caches aggressively
    pragma: "no-cache",
    "cache-control": "no-cache",
};

const defaultGetJsonHeaders: HeadersInit = {
    ...defaultHeaders,
    accept: "application/json",
};

const defaultPostJsonHeaders: HeadersInit = {
    ...defaultHeaders,
    accept: "application/json",
    "content-type": "application/json",
    method: "POST",
};

/**
 * The default options that will be passed to any call to the Fetch API.
 */
export const defaultOptions: Partial<RequestInit> = {
    // pass along cookie credentials when sending AJAX calls
    credentials: "include",
    headers: defaultHeaders,
};

const defaultGetJsonOptions: Partial<RequestInit> = {
    ...defaultOptions,
    method: "GET",
    headers: defaultGetJsonHeaders,
};

const defaultPostJsonOptions: Partial<RequestInit> = {
    ...defaultOptions,
    method: "POST",
    headers: defaultPostJsonHeaders,
};

export class JsonError extends Error {
    public kind = "json-error" as const;

    constructor(
        public request: string,
        public httpCode: number,
        public statusText: string,
        public content: any,
    ) {
        super(`${request} returned ${httpCode} ${statusText}\n${content.toString()}`);
    }
}

export const isJsonError = (error: Error | null | any): error is JsonError => {
    return error !== null && typeof error["kind"] !== "undefined" && error["kind"] === "json-error";
};

export class Fetcher implements IFetcher {
    fetch(request: RequestInfo, options?: RequestInit): Promise<Response> {
        options = options ? { ...defaultOptions, ...options, headers: { ...defaultHeaders, ...options.headers } } : defaultOptions;

        return self.fetch(request, options);
    }

    async getJSON<TResponseBody>(request: RequestInfo, options?: RequestInit): Promise<TResponseBody> {
        options = options ? { ...defaultGetJsonOptions, ...options, headers: { ...defaultGetJsonHeaders, ...options.headers } } : defaultGetJsonOptions;

        const response = await self.fetch(request, options);

        if (response.ok) return await response.json();

        const responseText = await response.text();
        throw new JsonError(request.toString(), response.status, response.statusText, responseText);
    }

    get(request: RequestInfo, options?: RequestInit): Promise<boolean> {
        options = options ? { ...defaultGetJsonOptions, ...options, headers: { ...defaultGetJsonHeaders, ...options.headers } } : defaultGetJsonOptions;

        return new Promise<boolean>((resolve, reject) => {
            self.fetch(request, options)
                .then((response) => resolve(response.ok))
                .catch((error) => reject(error));
        });
    }

    async post<TRequestBody>(request: RequestInfo, requestBody?: TRequestBody, options?: RequestInit): Promise<void> {
        const body = requestBody ? { body: JSON.stringify(requestBody) } : {};
        options = options
            ? { ...defaultPostJsonOptions, ...options, ...body, headers: { ...defaultPostJsonHeaders, ...options.headers } }
            : { ...defaultPostJsonOptions, ...body, headers: defaultPostJsonHeaders };

        let response: Response;

        try {
            response = await self.fetch(request, options);
        } catch (e) {
            if (options?.signal?.aborted) throw new AbortError();
            throw e;
        }

        if (response.ok) return;

        const responseJson = await response.json();
        throw new JsonError(request.toString(), response.status, response.statusText, responseJson);
    }

    async postJSON<TRequestBody, TResponseBody>(request: RequestInfo, requestBody?: TRequestBody, options?: RequestInit): Promise<TResponseBody> {
        const body = requestBody ? { body: JSON.stringify(requestBody) } : {};
        options = options
            ? { ...defaultPostJsonOptions, ...options, ...body, headers: { ...defaultPostJsonHeaders, ...options.headers } }
            : { ...defaultPostJsonOptions, ...body, headers: defaultPostJsonHeaders };

        let response: Response;

        try {
            response = await self.fetch(request, options);
        } catch (e) {
            if (options?.signal?.aborted) throw new AbortError();
            throw e;
        }

        if (response.ok) return await response.json();

        const responseText = await response.text();
        throw new Error(`${request} returned ${response.status} ${response.statusText}\n${responseText}`);
    }
}
